import { useCallback } from 'react';
import { AxiosError } from 'axios';
import { useTranslation } from 'next-i18next';
import { logger } from '@zep/core-logger';
import { type YoutubeVideoData } from '@/api/ai/youtube';
import { HttpStatusCodeEnum } from '@/api/httpClient';
import { useLocale } from '@/hooks';
import { LoadingSpinner } from '@/shared/components';
import { alertDialog, Button } from '@/ui_v3';
import { useFetchSubtitle } from '../hooks/useFetchSubtitle';
const MAX_DURATION_SECONDS = 60 * 60 * 2;
export const YoutubeConfirmButton = ({
  onConfirm,
  videoData
}: {
  onConfirm: (videoData: YoutubeVideoData) => void;
  videoData: YoutubeVideoData | null;
}) => {
  const {
    t
  } = useTranslation(['build', 'common']);
  const {
    locale
  } = useLocale();
  const {
    mutateAsync,
    isPending
  } = useFetchSubtitle();
  const handleConfirm = useCallback(async () => {
    if (!videoData) {
      return;
    }
    const startTime = Date.now();
    if (videoData.liveStatus !== 'none') {
      alertDialog.open({
        header: {
          title: t('build:build.ai.youtube.error.liveVideo')
        }
      });
      logger.track('quiz_youtube_url_validation_fail', {
        fail_reason: 'live_stream'
      });
      return;
    }
    if (videoData.durationSeconds > MAX_DURATION_SECONDS) {
      alertDialog.open({
        header: {
          title: t('build:youtube.durationExceed.title')
        },
        body: t('build:youtube.durationExceed.description')
      });
      logger.track('quiz_youtube_url_validation_fail', {
        fail_reason: 'over_duration'
      });
      return;
    }
    try {
      // AI 서버에 videoId 전달하고, 자막 데이터가 있으면 s3 업로드하는 전처리 로직 추가
      await mutateAsync({
        videoId: videoData.videoId,
        locale: locale
      });
    } catch (error) {
      if (error instanceof AxiosError && error.response?.status === HttpStatusCodeEnum.NOT_FOUND_404) {
        alertDialog.open({
          header: {
            title: t('build:build.ai.youtube.generate.error.noSubtitles')
          }
        });
        logger.track('quiz_youtube_url_validation_fail', {
          fail_reason: 'no_subtitle'
        });
        return;
      }
      alertDialog.open({
        header: {
          title: t('build:build.ai.youtube.generate.error.subtitlesLoadFailed')
        }
      });
      logger.track('quiz_youtube_url_validation_fail', {
        fail_reason: 'unknown'
      });
      return;
    }
    logger.track('quiz_youtube_url', {
      video_title: videoData.title,
      video_length_seconds: videoData.durationSeconds,
      latency_ms: Date.now() - startTime
    });
    return onConfirm(videoData);
  }, [videoData, t, onConfirm, mutateAsync, locale]);
  return <Button variant="contained" intent="strong" size="md" className="min-w-[120px]" leftSlot={isPending ? <LoadingSpinner className="size-sm" /> : undefined} disabled={!videoData || isPending} onClick={handleConfirm} data-sentry-element="Button" data-sentry-component="YoutubeConfirmButton" data-sentry-source-file="YoutubeConfirmButton.tsx">
      {t('common:add.youtube')}
    </Button>;
};